import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';
import type { PauseRequestDto, PauseResultDto } from '@tiffinify/contracts';
import { Link } from 'react-router';
import { addDays, formatDay, formatRanges, mealsOf, type DayTotals, type Meal } from '@tiffinify/core';
import { AppHeader, PageLoader, Banner, BottomNav, BtnSecondary, Card, ErrorText, Empty, Hero, Icon, IconBtn, MealIcon, Meter, Mono, RingAvatar, Section, LoadError } from '../components/ui';
import { FlashBanner } from '../lib/flash';
import { endLabel, errorMessage, formatInr, mealName, mealsLabel } from '../lib/format';
import { post } from '../lib/api';
import { useKitchenDay, useMe, useToday } from '../lib/queries';

function Receipt({ icon, name, n }: { icon: React.ReactNode; name: string; n: number }) {
  return (
    <div className="receipt">
      {icon}
      <span className="receipt-name">{name}</span>
      <span className="dots" aria-hidden="true" />
      <b>{n}</b>
    </div>
  );
}

/** Meals out on one day, for the meals the kitchen serves. */
const sum = (t: DayTotals, meals: Meal[]) => meals.reduce((n, m) => n + t[m], 0);

function Col({ label, totals, meals }: { label: string; totals: DayTotals; meals: Meal[] }) {
  return (
    <div className="today-col">
      <Mono muted>{label}</Mono>
      <div className="tot"><span className="big">{sum(totals, meals)}</span><small>meals</small></div>
      <div className="receipt-list">
        {meals.map((m) => <Receipt key={m} icon={<MealIcon meal={m} />} name={mealName(m)} n={totals[m]} />)}
      </div>
    </div>
  );
}

/** One meal's deliveries today: how many are marked, and the way into marking them. */
function DeliveryLine({ date, meal }: { date: string; meal: Meal }) {
  const k = useKitchenDay(date, meal);
  const p = k.data?.progress;
  const listed = p ? p.delivered + p.notHome + p.failed + p.pending : 0;
  if (p && listed === 0) return null;
  const misses = p ? [p.notHome > 0 && `${p.notHome} not home`, p.failed > 0 && `${p.failed} couldn’t deliver`].filter(Boolean).join(' · ') : '';
  const done = !!p && p.pending === 0;
  return (
    <Link to={`/delivery?meal=${meal}`} className="deliv-line" aria-label={`Mark ${mealName(meal).toLowerCase()} deliveries`}>
      <span className="grow">
        <span className="deliv-meal"><MealIcon meal={meal} size={15} /><b>{mealName(meal)}</b></span>
        <Meter value={p?.delivered ?? 0} total={listed || 1} />
        <small>{p ? `${p.delivered} of ${listed} delivered${misses ? ` · ${misses}` : ''}` : 'Counting…'}</small>
      </span>
      <span className={`deliv-cta${done ? ' done' : ''}`} aria-hidden="true">
        {done ? <><Icon name="check" size={16} sw={2.6} />Done</> : <>Mark<Icon name="right" size={16} sw={2.6} /></>}
      </span>
    </Link>
  );
}

/** A customer asked from their "My plan" page to skip days: the kitchen says yes (the usual pause) or no. */
function PauseRequests({ list }: { list: PauseRequestDto[] }) {
  const qc = useQueryClient();
  const [told, setTold] = useState<{ text: string; link: string; label: string } | null>(null);
  const answer = useMutation({
    mutationFn: ({ r, yes }: { r: PauseRequestDto; yes: boolean }) =>
      post<PauseResultDto | { message: { text: string; link: string } }>(`/pause-requests/${r.id}/${yes ? 'accept' : 'decline'}`),
    onSuccess: (res, { r, yes }) => {
      const first = r.name.split(' ')[0];
      setTold({ ...res.message, label: yes ? `Paused ${first}’s days. Send the confirmation on WhatsApp.` : `Declined for ${first}. Send the message on WhatsApp.` });
      void qc.invalidateQueries();
    },
  });
  return (
    <>
      {told && <Banner action={<a href={told.link} target="_blank" rel="noreferrer" onClick={() => setTold(null)}>Send</a>}>{told.label}</Banner>}
      {list.length > 0 && (
        <Card>
          {list.map((r) => {
            const busy = answer.isPending && answer.variables?.r.id === r.id;
            return (
              <div className="person pause-request" key={r.id}>
                <Link to={`/s/${r.subscriberId}`}>
                  <span className="pause-request-mark" aria-hidden="true"><Icon name="pause" size={16} sw={2.4} /></span>
                  <span className="who">
                    <b>{r.name}</b>
                    <small>Asks to pause {formatRanges(r.days)} · {mealsLabel(r.slot)}</small>
                  </span>
                </Link>
                <span className="pause-request-actions">
                  <BtnSecondary sm label="No" disabled={answer.isPending} onClick={() => answer.mutate({ r, yes: false })} />
                  <BtnSecondary sm ink label="Pause" icon="check" busy={busy && answer.variables?.yes} disabled={answer.isPending} onClick={() => answer.mutate({ r, yes: true })} />
                </span>
              </div>
            );
          })}
        </Card>
      )}
      <ErrorText>{answer.error ? errorMessage(answer.error) : ''}</ErrorText>
    </>
  );
}

export function Today() {
  const me = useMe();
  const today = useToday();
  const d = today.data;
  const kitchenName = me.data?.kitchen.name ?? '';

  const day = (iso: string) => formatDay(iso).toUpperCase().replace(/ (\d+) .*/, ' $1');
  const totalDue = d?.needsYou.reduce((s, n) => s + n.due, 0) ?? 0;

  return (
    <>
      <main className="main today">
        <AppHeader kitchenName={kitchenName} />
        <FlashBanner />
        {today.isPending ? (
          <PageLoader label="Packing today's tiffins" />
        ) : today.error || !d ? (
          <LoadError what="today’s list" onRetry={() => void today.refetch()} />
        ) : (
          <>
            <Link to="/kitchen" className="hero-link">
              <Hero label="Meals today and tomorrow">
                <div className="today-grid" style={{ '--n': String(Math.max(sum(d.today, mealsOf(d.meals)), sum(d.tomorrow, mealsOf(d.meals)))).length } as CSSProperties}>
                  <Col label={d.isDeliveryDay ? `Today · ${day(d.date)}` : `Today · ${day(d.date)} · off`} totals={d.today} meals={mealsOf(d.meals)} />
                  <span className="vr" aria-hidden="true" />
                  <Col label={`${d.tomorrow.date === addDays(d.date, 1) ? 'Tomorrow' : 'Next'} · ${day(d.tomorrow.date)}`} totals={d.tomorrow} meals={mealsOf(d.meals)} />
                </div>
              </Hero>
            </Link>

            {d.isDeliveryDay && mealsOf(d.meals).some((m) => d.today[m] > 0) && (
              <Section title="Deliveries today" meta={<Mono>Mark as they go</Mono>}>
                <Card>{mealsOf(d.meals).filter((m) => d.today[m] > 0).map((m) => <DeliveryLine key={m} date={d.date} meal={m} />)}</Card>
              </Section>
            )}

            <Section title="Needs you" meta={totalDue > 0 ? <Mono>{formatInr(totalDue)} due</Mono> : undefined}>
              <PauseRequests list={d.pauseRequests ?? []} />
              {d.needsYou.length === 0 && (d.pauseRequests ?? []).length > 0 ? null : d.needsYou.length === 0 ? (
                <Card><Empty title="All clear">No plans ending soon and nothing to collect.</Empty></Card>
              ) : (
                <Card>
                  {d.needsYou.map((n) => {
                    const ending = n.reasons.includes('ending');
                    const pay = n.reasons.includes('payment');
                    const frac = Math.max(0, Math.min(1, n.daysToEnd / 7));
                    return (
                      <div className="person" key={n.subscriptionId}>
                        <Link to={`/s/${n.subscriberId}`}>
                          <RingAvatar name={n.name} frac={ending ? frac : 0.5} color={ending ? 'var(--chilli)' : 'var(--due-ring)'} />
                          <span className="who">
                            <b>{n.name}</b>
                            <small>
                              {ending && <span className="st-end">{endLabel(n.daysToEnd, n.endDate)}</span>}
                              {ending && pay && ' · '}
                              {pay && <span className="st-due">{formatInr(n.due)} due</span>}
                            </small>
                          </span>
                        </Link>
                        <IconBtn icon="chat" label={`${pay && !ending ? 'Send' : 'Remind'} ${n.name.split(' ')[0]} on WhatsApp`} to={`/remind/${n.subscriptionId}?type=${ending ? 'renewal' : 'payment'}`} />
                      </div>
                    );
                  })}
                </Card>
              )}
              {d.needsYou.length > 1 && <BtnSecondary label={`Remind all ${d.needsYou.length}, one after another`} icon="chat" to="/remind-all" />}
            </Section>
          </>
        )}
      </main>
      <BottomNav />
    </>
  );
}
